/**
 * Drawer lateral del feed "What's new" — activado desde el ícono 🎁 en el header.
 * Todos los users autenticados lo ven. Sin tracking de leídos (feed sin estado).
 */
import { X, Gift, Calendar, ExternalLink, Sparkles } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { Portal } from '@/components/Portal'
import { Drawer } from '@/components/ds/Drawer'
import { IconButton } from '@/components/IconButton'
import {
  useUpdatesFeed, CATEGORY_STYLES, SCOPE_FLAGS,
  type PlatformUpdate,
} from '../hooks/usePlatformUpdates'

interface UpdatesDrawerProps {
  open: boolean
  onClose: () => void
}

export function UpdatesDrawer({ open, onClose }: UpdatesDrawerProps) {
  const { data, isLoading } = useUpdatesFeed(open)

  return (
    // 🔴 El Drawer del sistema NO va por portal y este se abre desde la barra
    // superior: sin el <Portal>, cualquier ancestro con `backdrop-filter` lo
    // volvería su bloque contenedor y el cajón quedaría recortado adentro.
    <Portal>
      <Drawer
        open={open}
        onClose={onClose}
        size={448}
        title={
          <span className="flex items-center gap-2">
            <Gift size={18} className="text-primary shrink-0" />
            Novedades en Clobi
          </span>
        }
        actions={
          <IconButton onClick={onClose} aria-label="Cerrar" icon={<X size={16} />} />
        }
      >
        <div className="space-y-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12 text-content-subtle">
              <Sparkles size={16} className="animate-pulse mr-2" /> Cargando…
            </div>
          )}

          {!isLoading && data && data.updates.length === 0 && (
            <div className="text-center py-12">
              <Sparkles size={40} className="mx-auto text-content-subtle mb-3" />
              <p className="text-sm text-content-muted">
                Todavía no hay novedades publicadas.
              </p>
            </div>
          )}

          {data?.updates.map((u) => (
            <UpdateCard key={u.id} update={u} />
          ))}
        </div>
      </Drawer>
    </Portal>
  )
}

// ─── Tarjeta individual ──────────────────────────────────────────────────────

function UpdateCard({ update: u }: { update: PlatformUpdate }) {
  const cat = CATEGORY_STYLES[u.category]
  const published = new Date(u.published_at)
  const dateStr = published.toLocaleDateString('es-CL', {
    day: 'numeric', month: 'short', year: 'numeric',
  })

  return (
    <article className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-hidden shadow-card">
      {/* Meta header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-divider flex-wrap">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${cat.cls}`}>
          {cat.label}
        </span>
        {u.scope.map((code) => {
          const s = SCOPE_FLAGS[code]
          return (
            <span
              key={code}
              className="inline-flex items-center gap-1 text-[11px] text-content-muted bg-surface-muted rounded-full px-2 py-0.5"
              title={s?.label}
            >
              <span aria-hidden="true">{s?.flag}</span>
              <span>{s?.label}</span>
            </span>
          )
        })}
        <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-content-subtle">
          <Calendar size={11} /> {dateStr}
        </span>
      </div>

      {/* Media */}
      {u.video_embed_url && (
        <div className="aspect-video bg-black">
          <iframe
            src={u.video_embed_url}
            title={u.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full border-0"
          />
        </div>
      )}
      {!u.video_embed_url && u.image_url && (
        <img
          src={u.image_url}
          alt={u.title}
          className="w-full h-auto max-h-64 object-cover"
          loading="lazy"
        />
      )}

      {/* Content */}
      <div className="px-4 py-4 space-y-2">
        <h3 className="text-base font-semibold text-content">{u.title}</h3>
        {u.body_markdown && (
          <div className="text-sm text-content-muted markdown-body">
            <ReactMarkdown
              components={{
                a: (props) => (
                  <a
                    {...props}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  />
                ),
                ul: (props) => <ul {...props} className="list-disc ml-5 space-y-1" />,
                ol: (props) => <ol {...props} className="list-decimal ml-5 space-y-1" />,
                p: (props) => <p {...props} className="mb-2" />,
                strong: (props) => <strong {...props} className="font-semibold text-content" />,
                code: (props) => (
                  <code {...props} className="px-1 py-0.5 text-xs bg-surface-muted rounded font-mono" />
                ),
              }}
            >
              {u.body_markdown}
            </ReactMarkdown>
          </div>
        )}

        {u.action_url && (
          <a
            href={u.action_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-primary text-on-primary text-xs font-medium rounded-lg hover:bg-primary-hover transition-colors"
          >
            {u.action_label || 'Saber más'}
            <ExternalLink size={11} />
          </a>
        )}
      </div>
    </article>
  )
}
